import classes from '~/config/classes'

import timings from '~/config/timings'

export default class VideoPlayer extends HTMLElement {
  constructor() {
    super()
    this.controls = {}
    this.volume = Number(this.dataset.volume) / 100
    this.autoplay = this.dataset.autoplay === 'true'
    this.videos = this.querySelectorAll('video')
    this.debounce = false

    // Current state
    this.playing = this.autoplay

    // Previous state for when hide and show are called
    this.wasPlaying = this.playing

    this.videos.forEach(video => {
      video.volume = this.volume
    })
  }

  connectedCallback() {
    this.initControls()
    this.initControlEvents()

    window.addEventListener(
      'resize',
      this.handleResize.bind(this),
      { passive: true },
    )
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize)
  }

  /**
   * Pause and mute component if hidden via breakpoints
   */
  handleResize() {
    if (this.debounce) {
      clearTimeout(this.debounce)
    }

    this.debounce = setTimeout(() => {
      if (this.isHidden(this)) {
        if (this.wasPlaying === null) {
          this.wasPlaying = this.playing
        }
        this.pause()
      } else if (this.wasPlaying) {
        this.play()
        this.wasPlaying = null
      }
      clearTimeout(this.debounce)
    }, timings.s)
  }

  /**
   * Grab all controls in custom element's markup
   */
  initControls() {
    this.querySelectorAll('button[data-action]').forEach(element => {
      const action = element.dataset.action || ''
      this.controls[action.toLowerCase()] = element
    })
  }

  /**
   * Init video controls
   */
  initControlEvents() {
    // Add click and space bar / enter listeners
    const clickEvents = ['keyup', 'click']
    Object.values(this.controls).forEach(button => {
      clickEvents.forEach(eventName => {
        button.addEventListener(eventName, (event) => {
          const action = event.currentTarget.dataset.action

          if (
            eventName === 'keyup' &&
            event.key !== 'Enter' &&
            event.key !== ' ' &&
            event.key !== 'Space Bar'
          ) {
            return
          }

          if (typeof this[action] !== 'function') {
            return
          }

          this.controlClicked = true
          this[action](event)
        })
      })
    })
  }

  /**
   * Play visible videos and show pause button
   */
  play() {
    this.videos.forEach(video => {
      if (this.isHidden(video)) {
        return
      }
      video.play().catch()
    })

    this.playing = true

    this.controls.play.classList.add(classes.hidden)
    this.controls.pause.classList.remove(classes.hidden)

    if (this.controlClicked) {
      this.controls.pause.focus()
      this.controlClicked = false
    }
  }

  /**
   * Pause all videos and show play button
   */
  pause() {
    this.videos.forEach(video => {
      video.pause()
    })

    this.playing = false

    this.controls.pause.classList.add(classes.hidden)
    this.controls.play.classList.remove(classes.hidden)

    if (this.controlClicked) {
      this.controls.play.focus()
      this.controlClicked = false
    }
  }

  /**
   * Mute all videos and show unmute button
   */
  mute() {
    if (!this.controls.unmute || !this.controls.mute) {
      return
    }
    this.videos.forEach(video => {
      video.muted = true
    })

    this.controls.unmute.classList.remove(classes.hidden)
    this.controls.mute.classList.add(classes.hidden)

    if (this.controlClicked) {
      this.controls.unmute.focus()
      this.controlClicked = false
    }
  }

  /**
   * Unmute visible videos and show mute button
   */
  unmute() {
    if (!this.controls.unmute || !this.controls.mute) {
      return
    }

    this.videos.forEach(video => {
      if (this.isHidden(video)) {
        return
      }
      video.muted = false
    })

    this.controls.unmute.classList.add(classes.hidden)
    this.controls.mute.classList.remove(classes.hidden)

    if (this.controlClicked) {
      this.controls.mute.focus()
      this.controlClicked = false
    }
  }

  /**
   * Check if element is hidden
   * @param element - Element to check
   * @returns {boolean}
   */
  isHidden(element) {
    if (!element) {
      return true
    }
    return element.clientWidth === 0 || Boolean(element.closest('[aria-hidden="true"]'))
  }

  /**
   * Hide this component, save state, and pause all videos
   * @param {Boolean} [options.hidden] - Add `is-hidden` class to this component
   * @param {Boolean} [options.ariaHidden] - Add `aria-hidden="true"` to this
   * component
   */
  hide(options = {}) {
    const config = {
      hidden: true,
      ariaHidden: true,
      ...options,
    }

    if (config.hidden) {
      this.classList.add(classes.hidden)
    }

    if (config.ariaHidden) {
      this.setAttribute('aria-hidden', 'true')
    }

    this.wasPlaying = this.playing
    this.pause()
  }

  /**
   * Show this component and play the video based on the previous state
   */
  show() {
    this.classList.remove(classes.hidden)
    this.removeAttribute('aria-hidden')

    if (this.wasPlaying) {
      this.play()
    }

    this.wasPlaying = null
  }
}
